"use client";

import { useEffect, useState, useRef } from "react";
import { Modal, Progress, Button, Row, Col, message, Select } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { getOAuthHeaders } from ".././constants/oAuthValidation";
import { upsertOfdOrderStatus } from "../services/index";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import messages from "../errors/messages.en-IN.json";

const { Option } = Select;

// One-time, app-lifetime unlock: browsers block audio.play() until the page
// has had a user gesture. Priming a silent play/pause on the first tap or
// keypress means the real order-sound play() later isn't the first attempt,
// so it isn't blocked. Fires once per page load, independent of any single
// notification instance.
let audioUnlockArmed = false;
if (typeof document !== "undefined") {
  const unlockAudio = () => {
    if (audioUnlockArmed) return;
    audioUnlockArmed = true;
    const primer = new Audio("/sounds/order.mp3");
    primer.volume = 0;
    primer.play().then(() => primer.pause()).catch(() => {});
    document.removeEventListener("pointerdown", unlockAudio);
    document.removeEventListener("keydown", unlockAudio);
  };
  document.addEventListener("pointerdown", unlockAudio);
  document.addEventListener("keydown", unlockAudio);
}

const OrderNotification = ({ orderData, index, onAutoAccept, onReject, onClose }) => {
  const authHeaders = getOAuthHeaders();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const [loading, setLoading] = useState(false);
  const [showRejectOptions, setShowRejectOptions] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const countdownTime = 30;
  const intervalRef = useRef(null);

  // TSK-3229: order.created now carries hasOutOfStock/outOfStockItems directly
  // (per the finalized backend contract), so this is a plain field read — no
  // extra stock-check round trip needed for the popup. Gated on the same
  // allowModificationInAggregator flag as the rest of the feature: off/absent
  // means this banner never renders, same as before TSK-3229 existed.
  const allowModification = usePOSConfigStore.getState().posConfig?.allowModificationInAggregator === "Y";
  const outOfStockItems = allowModification ? orderData?.data?.outOfStockItems || [] : [];

  const rejectionReasons = [
    "item_out_of_stock",
    "store_closed",
    "store_busy",
    "rider_not_available",
    "out_of_delivery_radius",
    "connectivity_issue",
    "total_missmatch",
    "invalid_item",
    "option_out_of_stock",
    "invalid_option",
    "unspecified",
  ];

  const clearCountdown = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (visible && orderData?.data) {
      const audio = new Audio("/sounds/order.mp3");
      audio.loop = true; // Enable looping
      let retryHandler = null;

      const clearRetry = () => {
        if (retryHandler) {
          document.removeEventListener("pointerdown", retryHandler);
          document.removeEventListener("keydown", retryHandler);
          retryHandler = null;
        }
      };

      const attemptPlay = () => {
        audio
          .play()
          .then(() => setSoundBlocked(false))
          .catch((err) => {
            console.warn("Autoplay prevented:", err);
            setSoundBlocked(true);
            if (!retryHandler) {
              retryHandler = () => {
                clearRetry();
                attemptPlay();
              };
              document.addEventListener("pointerdown", retryHandler, { once: true });
              document.addEventListener("keydown", retryHandler, { once: true });
            }
          });
      };

      attemptPlay();

      // Cleanup: Stop the audio when modal is closed or component unmounts
      return () => {
        clearRetry();
        audio.pause(); // Stop the audio
        audio.currentTime = 0; // Reset to start
      };
    }
  }, [visible, orderData]);

  useEffect(() => {
    if (!orderData?.data) return;

    let remainingTime = countdownTime;
    intervalRef.current = setInterval(() => {
      remainingTime -= 1;
      setProgress((remainingTime / countdownTime) * 100);

      if (remainingTime <= 0) {
        clearCountdown();
        handleAccept(true);
      }
    }, 1000);

    return () => clearCountdown();
  }, [orderData]);

  const updateOrderStatus = async (newStatus, reason = null, isOnline = "N") => {
    const setAuthTokens = authHeaders.access_token;
    const statusMapping = {
      Acknowledged: "AK",
      Cancelled: "CC",
      "Food Ready": "FR",
      Dispatched: "DI",
    };
    const statusCode = statusMapping[newStatus] || newStatus;
    setLoading(true);
    try {
      console.debug("[ORDER-ACCEPT-DIAG] updateOrderStatus → calling upsertOfdOrderStatus", { orderId: orderData?.data?.orderId, statusCode, reason, isOnline });
      const response = await upsertOfdOrderStatus(orderData?.data.orderId, statusCode, reason, null, isOnline);

      const result = response?.data?.data?.upsertOfdOrderStatus;
      console.debug("[ORDER-ACCEPT-DIAG] upsertOfdOrderStatus response", { status: result?.status, message: result?.message });
      if (result?.status === "200") {
        message.success(result?.message);
      } else {
        console.error("Status update failed:", result?.message);
        message.error(`${messages.errors.orders.status_update_failed}`);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(`${messages.errors.orders.status_update_failed}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (autoAccept) => {
    console.debug("[ORDER-ACCEPT-DIAG] handleAccept invoked", { autoAccept, orderId: orderData?.data?.orderId, externalId: orderData?.data?.externalId });
    clearCountdown();
    setVisible(false);
    if (!autoAccept) {
      await updateOrderStatus("Acknowledged");
    }
    if (onAutoAccept) {
      onAutoAccept({ isManualAccept: !autoAccept });
    }
  };

  const handleRejectClick = () => {
    setShowRejectOptions(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedReason) {
      message.error(`${messages.errors.orders.rejection_reason_required}`);
      return;
    }
    clearCountdown();
    setVisible(false);
    await updateOrderStatus("Cancelled", selectedReason);
    if (onReject) {
      onReject();
    }
    setShowRejectOptions(false);
    setSelectedReason(null);
  };
  const handleClose = () => {
    clearCountdown();
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!orderData?.data) return null;

  return (
    <>
      {/* Audio isn't guaranteed (browser autoplay policy can block the very
          first sound of a session before any gesture has happened), so a
          blocked buzzer must still be impossible to miss visually -- a
          pulsing border on the whole popup, not just a small text line. */}
      {soundBlocked && (
        <style>{`
          @keyframes cwSoundBlockedPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(180, 83, 9, 0.7); }
            50% { box-shadow: 0 0 0 8px rgba(180, 83, 9, 0); }
          }
        `}</style>
      )}
      <Modal
        open={visible}
        footer={null}
        closable={true}
        onCancel={handleClose}
        mask={false}
        centered={false}
        width={300}
        style={{
          top: 10 + index * 320,
          left: "50%",
          transform: "translateX(-50%)",
          position: "absolute",
          paddingTop: "0px",
          overflow: "hidden",
        }}
        styles={{
          content: soundBlocked
            ? { border: "3px solid #B45309", animation: "cwSoundBlockedPulse 1.2s infinite" }
            : {},
          body: {
            padding: "20px 15px",
            paddingTop: "0px",
            borderRadius: "10px",
          },
        }}
      >
      <div style={{ position: "absolute", top: 0, left: 0, width: "100%" }}>
        <Progress percent={progress} status="active" showInfo={false} strokeColor="#2F3856" strokeWidth={5} style={{ marginBottom: "0px", borderRadius: "10px 10px 0 0" }} />
      </div>

      <div style={{ textAlign: "left", padding: "20px 10px 10px" }}>
        <h3 style={{ fontSize: "16px", marginBottom: "5px", color: "#0F0718" }}>New Order #{orderData.data.orderNo}</h3>
        <p style={{ fontSize: "12px", marginBottom: "15px", color: "#504F53" }}>Order will be auto-confirmed in 30 seconds.</p>

        {soundBlocked && (
          <div style={{ fontSize: "12px", fontWeight: 700, color: "#FFFFFF", background: "#B45309", padding: "8px 10px", borderRadius: "6px", marginBottom: "10px" }}>
            🔇 SOUND BLOCKED — tap anywhere on screen to enable order alerts
          </div>
        )}

        {outOfStockItems.length > 0 && (
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: "6px",
              padding: "8px 10px",
              marginBottom: "12px",
              display: "flex",
              gap: "6px",
              alignItems: "flex-start",
            }}
          >
            <WarningOutlined style={{ color: "#DC2626", fontSize: "14px", flexShrink: 0, marginTop: "1px" }} />
            <div style={{ fontSize: "11px", color: "#991B1B" }}>
              <span style={{ fontWeight: 600 }}>
                {outOfStockItems.map((item) => (typeof item === "string" ? item : item?.name)).filter(Boolean).join(", ")}
              </span>{" "}
              {outOfStockItems.length > 1 ? "are" : "is"} currently <strong>out of stock</strong>. Accept and modify, or reject the order.
            </div>
          </div>
        )}

        {showRejectOptions ? (
          <>
            <Select style={{ width: "100%", marginBottom: "15px" }} placeholder="Select rejection reason" onChange={(value) => setSelectedReason(value)} value={selectedReason}>
              {rejectionReasons.map((reason) => (
                <Option key={reason} value={reason}>
                  {reason.replace(/_/g, " ")}
                </Option>
              ))}
            </Select>
            <Row justify="center" gutter={10}>
              <Col>
                <Button
                  type="default"
                  size="large"
                  onClick={() => setShowRejectOptions(false)}
                  style={{
                    color: "#2F3856",
                    background: "#FFFFFF",
                    borderRadius: "15px",
                    fontSize: "14px",
                    padding: "4px 8px",
                    border: "1px solid #2F3856",
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleRejectConfirm}
                  loading={loading}
                  style={{
                    color: "#FFFFFF",
                    background: "#2F3856",
                    borderRadius: "15px",
                    fontSize: "14px",
                    padding: "4px 8px",
                    border: "none",
                  }}
                >
                  Confirm Reject
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <Row justify="center" gutter={10}>
            <Col>
              <Button
                type="default"
                size="large"
                onClick={handleRejectClick}
                loading={loading}
                style={{
                  color: "#2F3856",
                  background: "#FFFFFF",
                  borderRadius: "15px",
                  fontSize: "14px",
                  padding: "4px 8px",
                  border: "1px solid #2F3856",
                }}
              >
                Reject
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  handleAccept(false);
                }}
                loading={loading}
                style={{
                  color: "#FFFFFF",
                  background: "#2F3856",
                  borderRadius: "15px",
                  fontSize: "14px",
                  padding: "4px 8px",
                  border: "none",
                }}
              >
                Accept
              </Button>
            </Col>
          </Row>
        )}
      </div>
      </Modal>
    </>
  );
};

export default OrderNotification;
